/**
 * =============================================================================
 * ROASTBREW - PLAYLIST EDITOR  ·  /playlist/:code/edit
 * =============================================================================
 *
 * Create, edit and remix are ONE page. What it opens with depends on the data:
 *   - a code that is one of your playlists → that playlist, ready to edit
 *   - arrived from Remix (router state { remixOf }) → a copy of that playlist
 *   - any other code → a brand-new, empty playlist
 *
 * On the page: Title and Author · Private / Public · the playlist code (Copy) ·
 * lessons and notes (drag to reorder, or ↑ ↓ on a row's grip; remove with Undo) ·
 * "Add a lesson" (library pop-up) · "Add a note" · Helpers: running time at
 * 1× / 1.5× / 2×, suggested next lessons, and a missing-step check.
 *
 * NOT WIRED YET — NOTHING IS SAVED. Every change lives on this page only, and the
 * status line says so. When the server exists this page autosaves: each change is
 * sent as it happens (title and author when the field loses focus), and the
 * status line becomes "Saving…" / "All changes saved".
 *
 * Content comes from data/playlistSample.js (lesson library = SAMPLE_LESSONS).
 * Access: gated in App.jsx (RequireFeature 'playlists'). No tier logic here.
 * =============================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import {
  SAMPLE_LESSONS, getSamplePlaylist, codeGroups, lengthToSeconds, formatMinutes,
} from './data/playlistSample';

const SANS = '"DM Sans", sans-serif';
const SERIF = '"Cormorant Garamond", serif';
const SR = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' };
const PENCIL = (size) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4L19 9l-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" />
  </svg>
);
const PLUS = (size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
);
const CROSS = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);
const GRIP = (
  <svg width="14" height="18" viewBox="0 0 14 18" fill="currentColor" aria-hidden="true">
    <circle cx="4" cy="3" r="1.6" /><circle cx="10" cy="3" r="1.6" /><circle cx="4" cy="9" r="1.6" /><circle cx="10" cy="9" r="1.6" /><circle cx="4" cy="15" r="1.6" /><circle cx="10" cy="15" r="1.6" />
  </svg>
);
const byId = Object.fromEntries(SAMPLE_LESSONS.map((l) => [l.videoId, l]));
const COURSES = ['All', ...Array.from(new Set(SAMPLE_LESSONS.map((l) => l.course)))];
let keySeq = 0;
const nextKey = () => `k${++keySeq}`;

// What the page opens with (see the header).
function initialPlaylist(code, remixOf, myName) {
  const toItems = (items) => items.map((it) => (it.type === 'note'
    ? { key: nextKey(), type: 'note', text: it.text }
    : { key: nextKey(), type: 'lesson', videoId: it.videoId }));
  if (remixOf) {
    const src = getSamplePlaylist(remixOf);
    if (src) return { title: `${src.title} (remix)`, author: '', visibility: 'private', items: toItems(src.items) };
  }
  const own = getSamplePlaylist(code);
  if (own && own.author === 'You') return { title: own.title, author: myName || '', visibility: own.visibility, items: toItems(own.items) };
  return { title: '', author: '', visibility: 'private', items: [] };
}

// Remount the editor whenever the playlist (or the remix source) changes, so one
// playlist's unsaved edits never carry over into another's.
export default function PlaylistEditPage(props) {
  const { code } = useParams();
  const location = useLocation();
  const remixOf = (location.state && location.state.remixOf) || '';
  return <PlaylistEditor key={`${code}|${remixOf}`} {...props} />;
}

function PlaylistEditor({ isMobile = false, isTablet = false }) {
  const { colors, isLight } = useTheme();
  const navigate = useNavigate();
  const { code } = useParams();
  const location = useLocation();
  const remixOf = location.state && location.state.remixOf;
  const { profile } = useAuth();

  // The page opens once per code (the wrapper above remounts it); a later name change must not reset edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const start = useMemo(() => initialPlaylist(code, remixOf, profile && profile.name), [code, remixOf]);
  const [title, setTitle] = useState(start.title);
  const [author, setAuthor] = useState(start.author);
  const [visibility, setVisibility] = useState(start.visibility);
  const [items, setItems] = useState(start.items);
  const [speed, setSpeed] = useState(1);
  const [drag, setDrag] = useState(null);       // index being dragged
  const [over, setOver] = useState(null);       // drop target index (items.length = the end)
  const [removed, setRemoved] = useState(null); // { item, at } — for Undo
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [course, setCourse] = useState('All');
  const [copied, setCopied] = useState(false);
  const [focusKey, setFocusKey] = useState(null);
  const undoTimer = useRef(null);
  const copyTimer = useRef(null);
  useEffect(() => () => { clearTimeout(undoTimer.current); clearTimeout(copyTimer.current); }, []);
  // Esc closes the library from anywhere while it's open. (Adding a lesson removes it from
  // the list, so the button just pressed disappears and focus can land outside the pop-up.)
  const searchRef = useRef(null);
  useEffect(() => {
    if (!addOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setAddOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [addOpen]);

  const edge = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const rule = isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
  const field = isLight ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.25)';
  const surface = colors.cardBg || colors.bgAlt;
  const onAccent = isLight ? '#FFFFFF' : '#0D0D0D';

  // ── Derived ──
  const lessonVids = items.filter((i) => i.type === 'lesson').map((i) => i.videoId);
  const totalSec = lessonVids.reduce((a, v) => a + lengthToSeconds(byId[v].length), 0);
  let lessonNo = 0;
  const rows = items.map((it) => ({ ...it, no: it.type === 'lesson' ? ++lessonNo : null, lesson: it.type === 'lesson' ? byId[it.videoId] : null }));

  // Missing step: a lesson from the same course that the catalog places between two
  // consecutive lessons here, but which isn't in the playlist. (Lessons only.)
  const catIndex = (v) => SAMPLE_LESSONS.findIndex((l) => l.videoId === v);
  let gap = null;
  for (let i = 0; i < lessonVids.length - 1 && !gap; i++) {
    const a = catIndex(lessonVids[i]), b = catIndex(lessonVids[i + 1]);
    for (let k = a + 1; k < b; k++) {
      const L = SAMPLE_LESSONS[k];
      if (L.course === SAMPLE_LESSONS[a].course && !lessonVids.includes(L.videoId)) {
        // Placed right after the earlier lesson, so any note before the later lesson stays
        // attached to the lesson it introduces.
        gap = { lesson: L, between: [i + 1, i + 2], at: items.findIndex((x) => x.videoId === lessonVids[i]) + 1 };
        break;
      }
    }
  }
  // Suggested next: stay in the last lesson's course — what follows it first, then the rest.
  const last = lessonVids.length ? byId[lessonVids[lessonVids.length - 1]] : null;
  const pool = last ? SAMPLE_LESSONS.filter((l) => l.course === last.course && !lessonVids.includes(l.videoId) && (!gap || l.videoId !== gap.lesson.videoId)) : [];
  const lastIdx = last ? catIndex(last.videoId) : -1;
  const suggest = [...pool.filter((l) => catIndex(l.videoId) > lastIdx), ...pool.filter((l) => catIndex(l.videoId) < lastIdx)].slice(0, 2);

  // ── Actions ──
  const insertLesson = (videoId, at = items.length) => {
    if (lessonVids.includes(videoId)) return;
    const next = items.slice(); next.splice(at, 0, { key: nextKey(), type: 'lesson', videoId }); setItems(next);
  };
  const addNote = () => { const key = nextKey(); setItems((xs) => [...xs, { key, type: 'note', text: '' }]); setFocusKey(key); };
  const setNote = (key, text) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, text } : x)));
  const move = (from, to) => {
    if (from === null || from === to || from + 1 === to) return;
    const next = items.slice(); const [x] = next.splice(from, 1); next.splice(to > from ? to - 1 : to, 0, x); setItems(next);
  };
  const remove = (at) => {
    clearTimeout(undoTimer.current);
    setRemoved({ item: items[at], at });
    setItems(items.filter((_, i) => i !== at));
    undoTimer.current = setTimeout(() => setRemoved(null), 6000);
  };
  const undo = () => {
    if (!removed) return;
    clearTimeout(undoTimer.current);
    const next = items.slice(); next.splice(Math.min(removed.at, next.length), 0, removed.item); setItems(next); setRemoved(null);
  };
  const copyCode = () => {
    // The clipboard can be missing or refuse permission; writeText then rejects. Catch the
    // rejection too (try/catch alone only covers a missing clipboard), so it never surfaces.
    try { const w = navigator.clipboard && navigator.clipboard.writeText(code); if (w && w.catch) w.catch(() => {}); } catch (e) { /* no clipboard */ }
    clearTimeout(copyTimer.current); setCopied(true); copyTimer.current = setTimeout(() => setCopied(false), 1800);
  };
  const onGripKey = (e, i) => {
    if (e.key === 'ArrowUp' && i > 0) { e.preventDefault(); move(i, i - 1); }
    if (e.key === 'ArrowDown' && i < items.length - 1) { e.preventDefault(); move(i, i + 2); }
  };
  const dragProps = (i) => ({
    draggable: true,
    onDragStart: (e) => { try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } catch (x) { /* older browsers */ } setDrag(i); },
    onDragOver: (e) => { e.preventDefault(); if (over !== i) setOver(i); },
    onDrop: (e) => { e.preventDefault(); move(drag, i); setDrag(null); setOver(null); },
    onDragEnd: () => { setDrag(null); setOver(null); },
  });

  // Library pop-up results
  const lq = query.trim().toLowerCase();
  const library = SAMPLE_LESSONS.filter((l) => !lessonVids.includes(l.videoId) && (course === 'All' || l.course === course)
    && (!lq || `${l.title} ${l.course} ${l.section}`.toLowerCase().includes(lq)));

  // ── Styles ──
  const rowStyle = (i, isNote) => ({
    display: 'grid', gridTemplateColumns: '36px 40px minmax(0, 1fr) 44px', alignItems: 'center', gap: '6px',
    minHeight: '64px', padding: isNote ? '10px 0' : '4px 0', borderBottom: `1px solid ${rule}`,
    borderTop: `2px solid ${over === i ? colors.accent : 'transparent'}`, opacity: drag === i ? 0.4 : 1,
    background: isNote ? `${colors.accent}0D` : 'transparent',
  });
  const iconBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', border: 0, borderRadius: '50%', background: 'transparent', color: colors.muted, cursor: 'pointer' };
  const gripBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '44px', border: 0, background: 'transparent', color: colors.muted, cursor: 'grab', opacity: 0.7 };
  const label11 = { fontFamily: SANS, fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.muted };
  const card = { display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px', borderRadius: '16px', background: surface, border: `1px solid ${edge}` };
  const chip = (on) => ({
    height: '40px', padding: '0 12px', borderRadius: '10px', fontFamily: SANS, fontSize: '12.5px', cursor: 'pointer',
    background: on ? colors.text : 'transparent', color: on ? colors.bg : colors.textSecondary, border: `1px solid ${on ? colors.text : edge}`, fontWeight: on ? 600 : 400,
  });
  const plusBtn = { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', borderRadius: '50%', border: `1px solid ${colors.accent}`, background: 'transparent', color: colors.accent, cursor: 'pointer' };

  return (
    <div className="pl-edit" style={{
      position: 'relative', padding: isMobile ? '80px 16px 64px' : isTablet ? '80px 32px 72px' : '48px 56px 80px',
      minHeight: '100vh', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
      background: colors.bg, color: colors.text, animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      {/* Top bar: back on the left; status and visibility on the right */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <button type="button" onClick={() => navigate('/playlist')} style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px', minHeight: '44px', padding: 0, border: 0, background: 'transparent',
          fontFamily: SANS, fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.muted, cursor: 'pointer',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          Playlists
        </button>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' }}>
          <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: SANS, fontSize: '13px', color: colors.muted }}>
            <span aria-hidden="true" style={{ width: '7px', height: '7px', borderRadius: '50%', background: colors.muted }} />
            Preview — changes aren’t saved yet
          </span>
          <div role="radiogroup" aria-label="Who can see it" style={{ display: 'flex', gap: '4px', padding: '4px', borderRadius: '999px', background: surface, border: `1px solid ${edge}` }}>
            {[['private', 'Private'], ['public', 'Public']].map(([v, label]) => (
              <button key={v} type="button" role="radio" aria-checked={visibility === v} onClick={() => setVisibility(v)} style={{
                height: '36px', padding: '0 18px', border: 0, borderRadius: '999px', fontFamily: SANS, fontSize: '13px', cursor: 'pointer',
                background: visibility === v ? colors.accent : 'transparent', color: visibility === v ? onAccent : colors.textSecondary, fontWeight: visibility === v ? 600 : 400,
              }}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '820px', margin: isMobile ? '36px auto 0' : '56px auto 0', display: 'flex', flexDirection: 'column', gap: isMobile ? '44px' : '56px' }}>
        {/* Header: Title, Author, playlist code */}
        <header style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '14px', maxWidth: '100%' }}>
            <label htmlFor="pl-title" style={{ flexShrink: 0, fontFamily: SERIF, fontStyle: 'italic', fontSize: isMobile ? '20px' : '26px', color: colors.muted }}>Title:</label>
            <input id="pl-title" className="pl-field" type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              size={Math.max(8, Math.ceil((title || 'Type a title here').length * 0.85) + 1)} placeholder="Type a title here"
              style={{ boxSizing: 'content-box', maxWidth: '100%', minWidth: 0, padding: '0 0 6px', border: 0, borderBottom: `1px dashed ${field}`, background: 'transparent', color: colors.text, fontFamily: SERIF, fontWeight: 300, fontSize: isMobile ? '36px' : '56px', lineHeight: 1.05, textAlign: 'center' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
            <label htmlFor="pl-author" style={{ flexShrink: 0, fontFamily: SERIF, fontStyle: 'italic', fontSize: '21px', color: colors.muted }}>Author:</label>
            <input id="pl-author" className="pl-field" type="text" value={author} onChange={(e) => setAuthor(e.target.value)}
              size={Math.max(6, Math.ceil((author || 'Type your name here').length * 0.9) + 1)} placeholder="Type your name here"
              style={{ maxWidth: '100%', padding: '2px 0 4px', border: 0, borderBottom: `1px dashed ${field}`, background: 'transparent', color: colors.textSecondary, fontFamily: SERIF, fontStyle: 'italic', fontSize: '21px', textAlign: 'center' }} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', columnGap: '12px', marginTop: '16px', padding: '0 4px 0 16px', borderRadius: '12px', border: `1px solid ${edge}` }}>
            <span style={{ ...label11, whiteSpace: 'nowrap' }}>Playlist code</span>
            <span aria-label={`Playlist code ${codeGroups(code).join(' ')}`} style={{ fontFamily: SANS, fontSize: '17px', fontWeight: 500, letterSpacing: '0.06em', fontVariantNumeric: 'tabular-nums', color: colors.text, whiteSpace: 'nowrap' }}>
              {codeGroups(code).map((g, i) => (
                <React.Fragment key={g + i}>
                  {i > 0 && <span aria-hidden="true" style={{ margin: '0 0.35em', fontWeight: 400, color: colors.muted, opacity: 0.6 }}>–</span>}
                  <span>{g}</span>
                </React.Fragment>
              ))}
            </span>
            <button type="button" onClick={copyCode} aria-label="Copy playlist code" style={{ minWidth: '72px', height: '44px', padding: '0 12px', border: 0, background: 'transparent', color: colors.accent, fontFamily: SANS, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
        </header>

        {/* Contents: lessons and notes */}
        <section aria-label="Contents" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
            <h2 style={{ margin: 0, fontFamily: SANS, fontSize: '12px', fontWeight: 600, letterSpacing: '0.24em', textTransform: 'uppercase', color: colors.muted }}>Contents</h2>
            <div style={{ flex: 1, height: '1px', background: edge }} />
            <span style={{ fontFamily: SANS, fontSize: '13px', color: colors.muted }}>{lessonVids.length} {lessonVids.length === 1 ? 'lesson' : 'lessons'} · {formatMinutes(totalSec)}</span>
          </div>

          {rows.length === 0 && (
            <p style={{ margin: '12px 0 4px', fontFamily: SANS, fontSize: '14px', lineHeight: 1.6, color: colors.muted }}>
              No lessons yet. Add a lesson to start, and add notes between lessons for anything students should know.
            </p>
          )}

          {rows.map((r, i) => (
            <div key={r.key} className="pl-row" {...dragProps(i)} style={rowStyle(i, r.type === 'note')}>
              <button type="button" className="pl-grip" onKeyDown={(e) => onGripKey(e, i)}
                aria-label={`Reorder ${r.type === 'note' ? 'this note' : r.lesson.title} (drag, or use the arrow keys)`} style={gripBtn}>{GRIP}</button>
              {r.type === 'lesson' ? (
                <>
                  <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: '24px', color: colors.accent }}>{r.no}.</span>
                  <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span style={{ fontFamily: SERIF, fontSize: '21px', lineHeight: 1.15 }}>{r.lesson.title}</span>
                    <span style={{ fontFamily: SANS, fontSize: '12px', color: colors.muted }}>{r.lesson.course} · {r.lesson.section} · {r.lesson.length}</span>
                  </span>
                </>
              ) : (
                <>
                  <span style={{ display: 'flex', justifyContent: 'center', alignSelf: 'start', paddingTop: '2px', color: colors.accent }}>{PENCIL(14)}</span>
                  <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: '8px' }}>
                    <span style={{ fontFamily: SANS, fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.accent }}>Note</span>
                    <textarea aria-label="Note for students" rows={2} autoFocus={focusKey === r.key} value={r.text} onChange={(e) => setNote(r.key, e.target.value)}
                      placeholder="Write a note for students — it appears between lessons"
                      style={{ boxSizing: 'border-box', width: '100%', minHeight: '56px', padding: '8px 12px', borderRadius: '10px', border: `1px solid ${edge}`, background: colors.bg, color: colors.text, fontFamily: SERIF, fontSize: '18px', lineHeight: 1.4, resize: 'vertical' }} />
                  </span>
                </>
              )}
              <button type="button" onClick={() => remove(i)} aria-label={`Remove ${r.type === 'note' ? 'this note' : r.lesson.title}`} style={iconBtn}>{CROSS}</button>
            </div>
          ))}

          {/* Drop here to move an item to the end */}
          <div onDragOver={(e) => { e.preventDefault(); if (over !== items.length) setOver(items.length); }}
            onDrop={(e) => { e.preventDefault(); move(drag, items.length); setDrag(null); setOver(null); }}
            style={{ height: '10px', borderTop: `2px solid ${over === items.length ? colors.accent : 'transparent'}` }} />

          <button type="button" className="pl-add" onClick={() => { setAddOpen(true); setQuery(''); }} aria-haspopup="dialog" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', minHeight: '64px', marginTop: '16px', borderRadius: '14px',
            border: `1.5px dashed ${field}`, background: 'transparent', color: colors.accent, fontFamily: SERIF, fontSize: '22px', cursor: 'pointer',
          }}>{PLUS(18)} Add a lesson</button>
          <button type="button" className="pl-add" onClick={addNote} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', minHeight: '52px', marginTop: '10px', borderRadius: '14px',
            border: `1px dashed ${edge}`, background: 'transparent', color: colors.textSecondary, fontFamily: SANS, fontSize: '14.5px', cursor: 'pointer',
          }}>{PENCIL(14)} Add a note</button>
        </section>

        {/* Helpers — a separate, quieter section below the playlist */}
        <section aria-label="Helpers" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ flex: 1, height: '1px', background: edge }} />
            <h2 style={{ margin: '0 -0.24em 0 0', fontFamily: SANS, fontSize: '12px', fontWeight: 600, letterSpacing: '0.24em', textTransform: 'uppercase', color: colors.muted }}>Helpers</h2>
            <div style={{ flex: 1, height: '1px', background: edge }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: '16px', alignItems: 'stretch' }}>
            {/* Running time at a playback speed */}
            <div style={card}>
              <span style={label11}>Running time</span>
              <span style={{ fontFamily: SERIF, fontSize: '36px', lineHeight: 1 }}>{formatMinutes(Math.round(totalSec / speed))}</span>
              <div style={{ height: '6px', borderRadius: '6px', background: edge, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.round(100 / speed)}%`, background: colors.accent }} />
              </div>
              <span style={{ fontFamily: SANS, fontSize: '13px', color: colors.textSecondary }}>
                {speed === 1 ? 'At normal speed' : `At ${speed}× · ${Math.round((totalSec - totalSec / speed) / 60)} min less than normal`}
              </span>
              <div role="radiogroup" aria-label="Playback speed" style={{ display: 'flex', gap: '6px' }}>
                {[1, 1.5, 2].map((s) => (
                  <button key={s} type="button" role="radio" aria-checked={speed === s} onClick={() => setSpeed(s)} style={{ ...chip(speed === s), flex: 1 }}>{s}×</button>
                ))}
              </div>
            </div>

            {/* Suggested next */}
            <div style={card}>
              <span style={label11}>Suggested next</span>
              {suggest.length === 0 && (
                <span style={{ fontFamily: SANS, fontSize: '13px', lineHeight: 1.5, color: colors.muted }}>
                  {last ? `Every ${last.course} lesson is already in this playlist.` : 'Add a lesson to see suggestions.'}
                </span>
              )}
              {suggest.map((l) => (
                <div key={l.videoId} style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '52px' }}>
                  <span style={{ flex: 1, minWidth: 0, fontFamily: SANS }}>
                    <span style={{ display: 'block', fontSize: '14px' }}>{l.title}</span>
                    <span style={{ display: 'block', marginTop: '2px', fontSize: '11.5px', color: colors.muted }}>
                      {catIndex(l.videoId) > lastIdx ? `Often watched after “${last.title}”` : `Also in ${l.course} · ${l.section}`} · {l.length}
                    </span>
                  </span>
                  <button type="button" onClick={() => insertLesson(l.videoId)} aria-label={`Add ${l.title}`} style={plusBtn}>{PLUS()}</button>
                </div>
              ))}
            </div>

            {/* Missing step — always present: a suggestion when a lesson is skipped, otherwise all clear */}
            {gap ? (
              <div style={{ ...card, background: `${colors.accent}12`, border: `1px solid ${colors.accent}4D` }}>
                <span style={{ ...label11, color: colors.accent }}>Missing step?</span>
                <span style={{ fontFamily: SANS, fontSize: '14px', lineHeight: 1.5, color: colors.text }}>
                  “{gap.lesson.title}” usually comes between lessons {gap.between[0]} and {gap.between[1]}.
                </span>
                <button type="button" onClick={() => insertLesson(gap.lesson.videoId, gap.at)} style={{
                  alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '8px', height: '44px', padding: '0 16px', borderRadius: '999px',
                  border: `1px solid ${colors.accent}`, background: 'transparent', color: colors.accent, fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
                }}>{PLUS(16)} Add it in place</button>
              </div>
            ) : (
              <div style={card}>
                <span style={label11}>Missing step?</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: SANS, fontSize: '14px', color: colors.text }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6F8F6A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
                  No gaps
                </span>
                <span style={{ fontFamily: SANS, fontSize: '13px', lineHeight: 1.5, color: colors.muted }}>
                  {lessonVids.length > 1 ? 'The lessons follow on from each other.' : 'Add two or more lessons to check for gaps.'}
                </span>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Undo, after removing a lesson or note */}
      {removed && (
        <div role="status" style={{
          position: 'sticky', bottom: '24px', zIndex: 10, width: 'fit-content', margin: '24px auto 0', display: 'flex', alignItems: 'center', gap: '16px',
          padding: '8px 8px 8px 20px', borderRadius: '999px', background: colors.text, color: colors.bg, boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
        }}>
          <span style={{ fontFamily: SANS, fontSize: '14px' }}>Removed {removed.item.type === 'note' ? 'the note' : `“${byId[removed.item.videoId].title}”`}</span>
          <button type="button" onClick={undo} style={{ height: '44px', padding: '0 18px', border: 0, borderRadius: '999px', background: colors.bg, color: colors.text, fontFamily: SANS, fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Undo</button>
        </div>
      )}

      {/* Add-a-lesson pop-up: the lesson library */}
      {addOpen && (
        <div onClick={() => setAddOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: isMobile ? '24px 12px' : '60px 24px', background: 'rgba(0,0,0,0.62)' }}>
          <div role="dialog" aria-modal="true" aria-labelledby="pl-lib-title" onClick={(e) => e.stopPropagation()}
            style={{ boxSizing: 'border-box', width: '100%', maxWidth: '560px', maxHeight: 'calc(100dvh - 120px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px', padding: '22px', borderRadius: '20px', background: surface, border: `1px solid ${edge}`, boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 id="pl-lib-title" style={{ margin: 0, fontFamily: SERIF, fontWeight: 400, fontSize: '26px' }}>Lesson library</h2>
              <button type="button" onClick={() => setAddOpen(false)} aria-label="Close" style={{ ...iconBtn, background: edge, color: colors.text }}>{CROSS}</button>
            </div>
            <label htmlFor="pl-lib-search" style={SR}>Search lessons</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '46px', padding: '0 14px', borderRadius: '10px', background: colors.bg, border: `1px solid ${edge}` }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.muted} strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
              <input id="pl-lib-search" ref={searchRef} type="search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search lessons"
                style={{ flex: 1, minWidth: 0, height: '100%', border: 0, outline: 'none', background: 'transparent', color: colors.text, fontFamily: SANS, fontSize: '14.5px' }} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {COURSES.map((c) => <button key={c} type="button" aria-pressed={course === c} onClick={() => setCourse(c)} style={{ ...chip(course === c), height: '36px', borderRadius: '999px' }}>{c}</button>)}
            </div>
            <span style={{ fontFamily: SANS, fontSize: '12px', color: colors.muted }}>Press + to add a lesson.</span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {library.length === 0 && <span style={{ padding: '20px 2px', fontFamily: SANS, fontSize: '14px', color: colors.muted }}>No lessons match. Try another word or course.</span>}
              {library.map((l) => (
                <div key={l.videoId} style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '58px', padding: '4px 2px', borderBottom: `1px solid ${rule}` }}>
                  <span style={{ flex: 1, minWidth: 0, fontFamily: SANS }}>
                    <span style={{ display: 'block', fontSize: '14px' }}>{l.title}</span>
                    <span style={{ display: 'block', marginTop: '2px', fontSize: '11.5px', color: colors.muted }}>{l.course} · {l.section} · {l.length}</span>
                  </span>
                  <button type="button" onClick={() => { insertLesson(l.videoId); if (searchRef.current) searchRef.current.focus(); }} aria-label={`Add ${l.title}`} style={plusBtn}>{PLUS()}</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setAddOpen(false)} style={{ alignSelf: 'flex-end', height: '46px', padding: '0 24px', border: 0, borderRadius: '999px', background: colors.accent, color: onAccent, fontFamily: SANS, fontSize: '14.5px', fontWeight: 600, cursor: 'pointer' }}>Done</button>
          </div>
        </div>
      )}

      <style>{`
        .pl-edit button:focus-visible, .pl-edit textarea:focus-visible { outline: 2px solid ${colors.accent}; outline-offset: 2px; }
        .pl-field:hover { border-bottom-color: ${colors.muted} !important; }
        .pl-field:focus { outline: none; border-bottom-color: ${colors.accent} !important; }
        .pl-field::placeholder, .pl-edit textarea::placeholder, #pl-lib-search::placeholder { color: ${colors.muted}; font-style: italic; opacity: 0.8; }
        .pl-row:hover .pl-grip { color: ${colors.accent}; opacity: 1; }
        .pl-add:hover { border-color: ${colors.accent} !important; color: ${colors.accent} !important; }
        @media (prefers-reduced-motion: reduce) { .pl-edit, .pl-edit * { animation: none !important; transition: none !important; } }
      `}</style>
    </div>
  );
}
